// utils/exportToExcel.js
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

function getExcelColumnLetter(colIndex) {
  let temp = "";
  while (colIndex > 0) {
    let mod = (colIndex - 1) % 26;
    temp = String.fromCharCode(65 + mod) + temp;
    colIndex = Math.floor((colIndex - mod) / 26);
  }
  return temp;
}

export const exportToExcel = async (data, fileName, title, subtitle) => {
  if (!data || data.length === 0) return alert("❌ Tidak ada data untuk diekspor.");

  // 🔹 Kelompokkan data berdasarkan lokasi kerja
  const groupedData = data.reduce((acc, item) => {
    const location = item["Lokasi Kerja"] || "Tanpa Lokasi";
    if (!acc[location]) acc[location] = [];
    acc[location].push(item);
    return acc;
  }, {});

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Data Laporan", {
    pageSetup: { orientation: "landscape", paperSize: 9 },
  });

  const headers = Object.keys(data[0]);
  const upperHeaders = headers.map((h) => h.toUpperCase());
  const totalCols = headers.length;

  // 🌿 Warna Hijau Soft & Aman Diprint
  const colors = {
    titleBg: "14532D",     // hijau tua lembut
    headerBg: "22C55E",    // hijau segar medium
    sectionBg: "BBF7D0",   // hijau pastel (lokasi)
    subtotalBg: "DCFCE7",  // hijau muda lembut
    totalBg: "166534",     // hijau gelap solid
    textWhite: "FFFFFF",
    textBlack: "111827",
    borderGray: "9CA3AF",
  };

  // 🧩 Judul
  const titleReport = title || "LAPORAN DATA ANGGOTA";
  const subtitleReport = subtitle || "Data ekspor dari sistem";
  const generatedDate = `Tanggal Ekspor: ${new Date().toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })}`;

  [[titleReport], [subtitleReport], [generatedDate], []].forEach((r) => sheet.addRow(r));

  ["A1", "A2", "A3"].forEach((ref, i) => {
    const colRange = `${getExcelColumnLetter(totalCols)}${i + 1}`;
    sheet.mergeCells(`A${i + 1}:${colRange}`);
    const cell = sheet.getCell(ref);
    cell.font = { bold: true, size: 14, color: { argb: colors.textWhite } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.titleBg } };
  });

  let totalKeseluruhan = 0;

  // 🔁 Loop tiap lokasi
  Object.entries(groupedData).forEach(([location, members], index) => {
    if (index > 0) sheet.addRow([]);

    // 🏢 Header Lokasi
    const locRow = sheet.addRow([`LOKASI KERJA: ${location.toUpperCase()}`]);
    const endCol = getExcelColumnLetter(totalCols);
    sheet.mergeCells(`A${locRow.number}:${endCol}${locRow.number}`);
    locRow.getCell(1).font = { bold: true, size: 12, color: { argb: colors.textBlack } };
    locRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
    locRow.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.sectionBg } };

    // 🧾 Header Kolom
    const headerRow = sheet.addRow(upperHeaders);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: colors.textWhite } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.headerBg } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin", color: { argb: colors.borderGray } },
        left: { style: "thin", color: { argb: colors.borderGray } },
        bottom: { style: "thin", color: { argb: colors.borderGray } },
        right: { style: "thin", color: { argb: colors.borderGray } },
      };
    });

    // 📄 Isi Data
    members.forEach((item) => {
      const row = headers.map((h) => item[h] || "");
      const dataRow = sheet.addRow(row);
      dataRow.eachCell((cell) => {
        cell.font = { size: 11, color: { argb: colors.textBlack } };
        cell.alignment = { vertical: "middle", wrapText: true };
      });
    });

    // ✅ Subtotal (Tampil Jelas)
    const jumlah = members.length;
    const subtotalRow = sheet.addRow([`Anggota di ${location}`, jumlah]);
    subtotalRow.getCell(1).font = { bold: true, color: { argb: colors.textBlack } };
    subtotalRow.getCell(2).font = { bold: true, color: { argb: colors.textBlack } };
    subtotalRow.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.subtotalBg } };
    subtotalRow.getCell(2).fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.subtotalBg } };

    totalKeseluruhan += jumlah;
    sheet.addRow([]);
  });

  // ✅ Total Keseluruhan (Hijau Gelap Elegan)
  const totalRow = sheet.addRow(["TOTAL KESELURUHAN ANGGOTA", totalKeseluruhan]);
  totalRow.getCell(1).font = { bold: true, color: { argb: colors.textWhite } };
  totalRow.getCell(2).font = { bold: true, color: { argb: colors.textWhite } };
  totalRow.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.totalBg } };
  totalRow.getCell(2).fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.totalBg } };
  totalRow.getCell(2).alignment = { horizontal: "center", vertical: "middle" };

  // 📏 Kolom Lebar Rapi
  sheet.columns = headers.map(() => ({ width: 20 }));

  // 🧩 Footer
  sheet.addRow([]);
  const footer = sheet.addRow([`Dibuat oleh Sistem Soney Raya • ${new Date().toLocaleString("id-ID")}`]);
  const footerEnd = getExcelColumnLetter(totalCols);
  sheet.mergeCells(`A${footer.number}:${footerEnd}${footer.number}`);
  footer.font = { italic: true, size: 10, color: { argb: "6B7280" } };
  footer.alignment = { horizontal: "right" };

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${fileName}.xlsx`);
};
